import React, { useEffect } from 'react';

import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import type { InvestmentSymbolLookupItem } from 'app-components/flux/investment-holdings-v2';
import type { securitiesTypes } from 'app-components/flux/securities';
import type { tagsTypes } from 'app-components/flux/tags';
import { transactionsUtils } from 'app-components/flux/transactions';

import Box from '@mui/material/Box';
import { List } from 'immutable';

import MuiTagsField from 'components/QuickenControls/TagsField/TagsField';
import SymbolLookupField from 'components/SymbolLookupField';
import { useAppSelector } from 'hooks/redux';

import type { InvestmentTransactionFormikProps } from './types';
import { toTransactionProps } from './types';

type TagValue = tagsTypes.Tag | tagsTypes.TagProps;
type SecurityWithContract = securitiesTypes.SecurityProps & {
  sharesPerContract?: number;
};
type SymbolLookupItemWithContract = Omit<InvestmentSymbolLookupItem, 'security'> & {
  security?: SecurityWithContract;
};

const InvestmentTransactionBaseForm = (props: InvestmentTransactionFormikProps) => {
  const { formik } = props;

  useEffect(() => {
    formik.registerField('securityName', {
      validate: (value: string | null | undefined) =>
        value || formik.values.symbol || formik.values.localSecurityId ? undefined : 'required',
    });
    return () => {
      formik.unregisterField('securityName');
    };
  }, [formik]);

  const securitiesEnabled = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'securities'));

  const { tags } = formik.values;
  const tagsValue = Array.isArray(tags) ? tags : tags?.toArray() || [];

  return (
    <Box display='flex' gap={2}>
      <SymbolLookupField
        id='symbol-lookup'
        label='Security'
        sx={{ width: 400 }}
        hideCreateOption={!securitiesEnabled}
        value={transactionsUtils.security(toTransactionProps(formik.values), undefined)}
        onSelected={(item: SymbolLookupItemWithContract) => {
          formik.setFieldValue('fullSecurity', item); // For security creation
          formik.setFieldValue('symbol', item.symbol || '');
          formik.setFieldValue('securityName', item.name || '');
          formik.setFieldValue('localSecurityId', item.security?.id || '');
          formik.setFieldValue('securityIdType', item.security?.securityIdType || '');
          formik.setFieldValue('securityId', item.security?.securityId || '');
          formik.setFieldValue('sharesPerContract', item.security?.sharesPerContract);
        }}
        error={Boolean(formik.submitCount > 0 && formik.errors.securityName)}
        helperText={(formik.submitCount > 0 && formik.errors.securityName) || ''}
        onFocus={(event: React.FocusEvent<HTMLInputElement>) => event.target.select()}
        autoFocus
      />
      <MuiTagsField
        id='tags'
        value={tagsValue}
        onChange={(_event: React.SyntheticEvent, tagArray: TagValue[]) => formik.setFieldValue('tags', List(tagArray))}
      />
    </Box>
  );
};

export default InvestmentTransactionBaseForm;
